import { useEffect } from "react"

type AnimeModule = {
  animate?: (targets: unknown, parameters: Record<string, unknown>) => unknown
  createScope?: (parameters: Record<string, unknown>) => { add: (callback: () => void) => void; revert?: () => void }
  createAnimatable?: (targets: unknown, parameters: Record<string, unknown>) => Record<string, (value: unknown) => void>
  createLayout?: (targets: unknown) => { update?: (callback: (ctx: { root: HTMLElement }) => void, parameters: Record<string, unknown>) => unknown }
  stagger?: (value: number | string) => unknown
  spring?: (parameters: Record<string, unknown>) => unknown
}

export function useLandingMotion(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return
    let disposed = false
    let cleanup = () => {}

    async function wire() {
      const anime = (await import("animejs")) as AnimeModule
      if (disposed) return
      const { animate, createScope, createAnimatable, createLayout, stagger, spring } = anime

      const scope = createScope?.({ root: document.body })
      scope?.add(() => {
        animate?.("[data-motion='reveal']", {
          opacity: [0, 1],
          translateY: [18, 0],
          duration: 900,
          delay: stagger?.(90),
          ease: "out(3)",
        })
        animate?.(".cta", {
          scale: [0.985, 1],
          duration: 900,
          delay: stagger?.(80),
          ease: spring?.({ bounce: 0.28 }) || "out(3)",
        })
      })

      const spot = document.getElementById("spotlight")
      const animatable = spot && createAnimatable?.(spot, {
        "--mx": 420,
        "--my": 420,
        ease: "out(3)",
      })
      const move = (event: PointerEvent) => {
        animatable?.["--mx"]?.(`${event.clientX}px`)
        animatable?.["--my"]?.(`${event.clientY}px`)
      }
      if (animatable) window.addEventListener("pointermove", move, { passive: true })

      const layoutRoot = document.querySelector<HTMLElement>(".method-grid")
      const layout = layoutRoot && createLayout?.(layoutRoot)
      let i = 0
      let layoutTimer = 0
      const pulseLayout = () => {
        if (!layoutRoot || disposed) return
        layout?.update?.(({ root }) => {
          root.dataset.grid = String((++i % 4) + 1)
        }, {
          duration: 1000,
          delay: stagger?.(75),
          ease: "out(2)",
        })
        layoutTimer = window.setTimeout(pulseLayout, 4200)
      }
      pulseLayout()

      cleanup = () => {
        window.clearTimeout(layoutTimer)
        if (animatable) window.removeEventListener("pointermove", move)
        scope?.revert?.()
      }
    }

    wire().catch(() => {
      cleanup = () => {}
    })

    return () => {
      disposed = true
      cleanup()
    }
  }, [enabled])
}
